import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import PublicLayout from '@/components/hydlib/PublicLayout';
import PaymentsPanel from '@/components/PaymentsPanel';
import { useAuth } from '@/context/AuthContext';
import { gcalUrl } from '@/lib/gcal';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }); } catch { return iso; } };
const daysUntil = (iso) => { if (!iso) return null; const d = Math.ceil((new Date(iso) - new Date()) / (1000*60*60*24)); return d; };
const monthYear = (iso) => { try { return new Date(iso).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' }); } catch { return ''; } };

export default function MyLibrary() {
    const { user, getAuthHeader } = useAuth();
    const [searchParams] = useSearchParams();
    const payPlan = searchParams.get('plan');
    const [openSection, setOpenSection] = useState((payPlan || searchParams.get('section') === 'payments') ? 'payments' : '');

    const [myHolds, setMyHolds] = useState({ pending: [], reserved: [], borrowed: [], suspended: [], queued: [], delivered: [], returned: [] });
    const [recsForMe, setRecsForMe] = useState([]);
    const navigate = useNavigate();
    const [tbr, setTbr] = useState([]);
    const [recs, setRecs] = useState([]);
    const [events, setEvents] = useState([]);
    const [payments, setPayments] = useState([]);
    const [msg, setMsg] = useState('');
    const [recPending, setRecPending] = useState(false);
    const [askOpen, setAskOpen] = useState(false);
    const [recPrefs, setRecPrefs] = useState('');
    const [askSubmitting, setAskSubmitting] = useState(false);
    const [reviewHold, setReviewHold] = useState(null);
    const [extendHold, setExtendHold] = useState(null);
    const [reviewRating, setReviewRating] = useState(5);
    const [reviewText, setReviewText] = useState('');
    const [reviewSubmitting, setReviewSubmitting] = useState(false);

    const submitReview = async () => {
        if (!reviewHold?.book?.id) return;
        setReviewSubmitting(true);
        try {
            await axios.post(`${API}/reviews`,
                { book_id: reviewHold.book.id, rating: reviewRating, review_text: reviewText.trim() },
                { headers: getAuthHeader() });
            setMsg('Review submitted — it will appear once the librarian approves it.');
            setReviewHold(null);
            setReviewRating(5);
            setReviewText('');
        } catch (e) {
            setMsg(e.response?.data?.detail || 'Could not submit review.');
            setReviewHold(null);
        } finally {
            setReviewSubmitting(false);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const fetchHolds = useCallback(async () => {
        try {
            const [activeRes, historyRes] = await Promise.all([
                axios.get(`${API}/holds/my-holds`, { headers: getAuthHeader() }),
                axios.get(`${API}/holds/history`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
            ]);
            const grouped = { pending: [], reserved: [], borrowed: [], suspended: [], queued: [], delivered: [], returned: [] };
            for (const h of (activeRes.data || [])) {
                if (grouped[h.status]) grouped[h.status].push(h);
            }
            grouped.returned = historyRes.data || [];
            // Enrich each with book
            const enrich = async (arr) => Promise.all(arr.map(async (h) => {
                try {
                    const b = await axios.get(`${API}/books/${h.book_id}`, { headers: getAuthHeader() });
                    return { ...h, book: b.data };
                } catch { return { ...h, book: null }; }
            }));
            for (const k of Object.keys(grouped)) grouped[k] = await enrich(grouped[k]);
            setMyHolds(grouped);
        } catch { /* ignore */ }
    }, [getAuthHeader]);

    useEffect(() => {
        axios.get(`${API}/books/recommended-for-me`, { headers: getAuthHeader() })
            .then(r => setRecsForMe((r.data || []).map(b => ({ id: `rec-${b.id}`, book: b, reason: b.reason }))))
            .catch(() => { /* ignore */ });
    }, [getAuthHeader]);

    const fetchTbr = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/tbr`, { headers: getAuthHeader() });
            setTbr(res.data || []);
        } catch { /* ignore */ }
    }, [getAuthHeader]);

    const fetchRecs = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/recommendation-requests/my-recommendations`, { headers: getAuthHeader() });
            // API returns { recommendations: [...] } — array of recommended book objects
            const raw = Array.isArray(res.data) ? res.data : (res.data?.recommendations || []);
            setRecs(raw);
        } catch { /* ignore */ }
        try {
            const pending = await axios.get(`${API}/recommendation-requests/my-request`, { headers: getAuthHeader() });
            setRecPending(!!pending.data?.has_active_request);
        } catch { /* ignore */ }
    }, [getAuthHeader]);

    const askLibrarian = async () => {
        setAskSubmitting(true);
        try {
            await axios.post(`${API}/recommendation-requests`,
                { preferences: recPrefs.trim() || 'Based on my reading history' },
                { headers: getAuthHeader() });
            setRecPending(true);
            setAskOpen(false);
            setRecPrefs('');
        } catch (e) {
            setMsg(e.response?.data?.detail || 'Could not send request.');
        } finally {
            setAskSubmitting(false);
        }
    };

    const fetchEvents = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/addons/upcoming`, { headers: getAuthHeader() });
            const subscribed = (res.data?.upcoming || []).filter(a => a.is_subscribed);
            setEvents(subscribed);
        } catch { /* ignore */ }
    }, [getAuthHeader]);

    const fetchPayments = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/payments/history`, { headers: getAuthHeader() });
            setPayments((res.data || []).slice(0, 8));
        } catch { /* ignore */ }
    }, [getAuthHeader]);

    useEffect(() => {
        fetchHolds();
        fetchTbr();
        fetchRecs();
        fetchEvents();
        fetchPayments();
    }, [fetchHolds, fetchTbr, fetchRecs, fetchEvents, fetchPayments]);

    useEffect(() => {
        const onPaymentsUpdated = () => fetchPayments();
        window.addEventListener('payments-updated', onPaymentsUpdated);
        return () => window.removeEventListener('payments-updated', onPaymentsUpdated);
    }, [fetchPayments]);

    const cancelHold = async (hid) => {
        if (!window.confirm('Cancel this hold?')) return;
        try {
            await axios.delete(`${API}/holds/${hid}`, { headers: getAuthHeader() });
            setMsg('Hold cancelled.');
            fetchHolds();
        } catch (e) { setMsg(e.response?.data?.detail || 'Could not cancel hold.'); }
    };

    const requestExtension = async (hid, days) => {
        try {
            const res = await axios.post(`${API}/holds/${hid}/request-extension`, { days }, { headers: getAuthHeader() });
            if (res.data?.requires_payment) {
                // Rental extension: open Razorpay checkout — extension applies only after payment
                const { razorpay_order_id, razorpay_key_id, amount, book_title, extension_days, rental_price } = res.data;
                setMsg(`Pay ₹${rental_price} to confirm your ${extension_days}-day extension.`);
                const rzp = new window.Razorpay({
                    key: razorpay_key_id, amount, currency: 'INR', order_id: razorpay_order_id,
                    name: 'The Hyd Library',
                    description: `Extend "${book_title}" — ${extension_days} more days`,
                    handler: async (response) => {
                        try {
                            const v = await axios.post(`${API}/holds/${hid}/pay-extension`, {
                                razorpay_order_id: response.razorpay_order_id,
                                razorpay_payment_id: response.razorpay_payment_id,
                                razorpay_signature: response.razorpay_signature,
                            }, { headers: getAuthHeader() });
                            setMsg(v.data?.message || 'Extension paid & applied!');
                            fetchHolds();
                        } catch (err) {
                            setMsg(err.response?.data?.detail || 'Payment verification failed — please try again.');
                        }
                    },
                    prefill: { name: user?.name, email: user?.email },
                    theme: { color: '#31435f' },
                });
                rzp.open();
            } else {
                setMsg(res.data?.message || 'Extension requested.');
            }
            fetchHolds();
        } catch (e) { setMsg(e.response?.data?.detail || 'Could not extend.'); }
        setExtendHold(null);
    };

    const removeTbr = async (bid) => {
        try {
            await axios.delete(`${API}/tbr/${bid}`, { headers: getAuthHeader() });
            fetchTbr();
        } catch { /* ignore */ }
    };

    // Subscription status
    const memberSince = user?.subscription_start_date || user?.created_at;
    const booksOut = [...(myHolds.borrowed || []), ...(myHolds.delivered || [])].filter((h) => !h.is_child_hold).length;

    // Shelves data
    const notChild = (h) => !h.is_child_hold;
    const isChildHold = (h) => !!h.is_child_hold;
    const childShelfBooks = [
        ...(myHolds.borrowed || []), ...(myHolds.delivered || []),
        ...(myHolds.reserved || []), ...(myHolds.pending || []), ...(myHolds.queued || []),
        ...(myHolds.returned || []),
    ].filter(isChildHold);
    const dueMeta = (h) => {
        const d = daysUntil(h.due_date);
        return d == null ? 'Due date pending' : (d >= 0 ? `Due in ${d} day${d === 1 ? '' : 's'}` : `${Math.abs(d)} day${Math.abs(d) === 1 ? '' : 's'} overdue`);
    };
    const nextUpMeta = (h) => {
        if (h.status === 'reserved') return `Pickup by ${fmtDate(h.reservation_expiry_date || h.requested_pickup_date)}`;
        if (h.status === 'queued') return h.queue_position ? `#${h.queue_position} in queue` : 'In queue';
        if (h.delivery_option === 'delivery') return 'Coming by delivery';
        if (h.queue_position === 0) return `Available on ${fmtDate(h.requested_pickup_date)}`;
        return h.queue_position ? `#${h.queue_position} in queue` : 'In your queue';
    };
    const shelves = [
        {
            name: 'Currently reading',
            note: 'the pile that is actually moving',
            noteColor: 'var(--a3)',
            emptyText: 'No books out right now — your next read is just a hold away.',
            books: [...myHolds.borrowed, ...myHolds.delivered].filter(notChild),
            getMeta: dueMeta,
            getMetaColor: (h) => { const d = daysUntil(h.due_date); if (d != null && d < 0) return 'var(--a4)'; if (d != null && d <= 3) return 'var(--a2)'; return 'var(--a3)'; },
            getAction: (h) => ({ label: 'Extend', onClick: () => setExtendHold(h) }),
        },
        {
            name: 'Next up',
            note: 'reserved, queued & on the way',
            noteColor: 'var(--a5)',
            emptyText: 'Nothing reserved — browse the catalog to queue your next book.',
            books: [...myHolds.reserved, ...myHolds.pending, ...myHolds.queued].filter(notChild),
            getMeta: nextUpMeta,
            getMetaColor: (h) => (h.status === 'reserved' ? 'var(--a2)' : 'var(--a5)'),
            getAction: (h) => (h.status === 'reserved' ? null : { label: 'Cancel', onClick: () => cancelHold(h.id) }),
        },
        ...((childShelfBooks.length || user?.child_membership_status === 'active') ? [(() => {
            const finishedCount = childShelfBooks.filter((h) => h.status === 'returned').length;
            const MILESTONES = [5, 10, 25];
            const achieved = [...MILESTONES].reverse().find((m) => finishedCount >= m);
            const next = MILESTONES.find((m) => finishedCount < m);
            return {
            name: `${user?.child_name || 'My child'}'s shelf`,
            note: "the little one's reading journey · children's membership",
            noteColor: 'var(--a5)',
            badge: (achieved || next) ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    {achieved && (
                        <span data-testid="child-milestone-badge" className="hydlib-highlight" style={{ fontFamily: "'Space Mono', monospace", fontWeight: 700, fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', padding: '4px 10px', border: '2px solid var(--a3)', color: 'var(--a3)', transform: 'rotate(-2deg)', display: 'inline-block' }}>
                            🏅 {achieved} book{achieved === 1 ? '' : 's'} finished!
                        </span>
                    )}
                    {next && (
                        <span data-testid="child-milestone-progress" style={{ fontFamily: "'Caveat', cursive", fontSize: 17, color: 'var(--a1)' }}>
                            {next - finishedCount} more to reach {next} ✨
                        </span>
                    )}
                </span>
            ) : null,
            books: childShelfBooks,
            getMeta: (h) => {
                if (h.status === 'returned') return h.return_date ? `Returned ${fmtDate(h.return_date)}` : 'Returned';
                if (h.status === 'borrowed' || h.status === 'delivered') return dueMeta(h);
                return nextUpMeta(h);
            },
            getMetaColor: (h) => (h.status === 'returned' ? 'var(--a1)' : 'var(--a5)'),
            getAction: (h) => {
                if (h.status === 'borrowed' || h.status === 'delivered') return { label: 'Extend', onClick: () => setExtendHold(h) };
                if (h.status === 'pending' || h.status === 'queued') return { label: 'Cancel', onClick: () => cancelHold(h.id) };
                if (h.status === 'returned' && h.book?.id) return { label: 'Leave a review', onClick: () => setReviewHold(h) };
                return null;
            },
        };})()] : []),
        {
            name: 'Finished',
            note: 'the ones you conquered',
            noteColor: 'var(--a1)',
            emptyText: 'Books you return will land here — your personal reading history.',
            books: (myHolds.returned || []).filter(notChild),
            getMeta: (h) => (h.return_date ? `Returned ${fmtDate(h.return_date)}` : 'Returned'),
            getMetaColor: () => 'var(--a1)',
            getAction: (h) => (h.book?.id ? { label: 'Leave a review', onClick: () => setReviewHold(h) } : null),
        },
        {
            name: 'Recommended for you',
            note: 'based on your reading',
            noteColor: 'var(--a2)',
            emptyText: "Borrow a few books and we'll start picking recommendations for you.",
            books: recsForMe,
            getMeta: (h) => h.reason || '',
            getMetaColor: () => 'var(--a2)',
            getAction: (h) => (h.book?.id ? { label: 'Reserve →', onClick: () => navigate(`/book/${h.book.id}`) } : null),
        },
    ];

    if (myHolds.suspended?.length) {
        shelves.push({
            name: 'Suspended',
            note: 'reactivate to keep your spot',
            noteColor: 'var(--a4)',
            books: myHolds.suspended,
            getMeta: () => 'Suspended — reactivate below',
            getMetaColor: () => 'var(--a4)',
            getAction: (h) => ({ label: 'Reactivate', onClick: async () => {
                try { await axios.post(`${API}/holds/${h.id}/unsuspend`, {}, { headers: getAuthHeader() }); fetchHolds(); }
                catch (e) { setMsg(e.response?.data?.detail || 'Could not reactivate.'); }
            }}),
        });
    }

    return (
        <PublicLayout theme="archive" grid="--a1" gridStrength={32}>
            <div className="hydlib-scallop" style={{ backgroundImage: 'radial-gradient(circle at 50% 0, var(--a5) 0 5px, transparent 6px)' }} />
            <div className="hydlib-pad" style={{ maxWidth: 1240, margin: '0 auto', padding: '56px 48px 72px' }}>
                <h1 data-testid="mylib-header-title" style={{ fontFamily: "'Libre Caslon Display', serif", fontWeight: 400, fontSize: 52, margin: '0 0 8px' }}>
                    {user?.name ? `Welcome back, ${user.name.split(' ')[0]}` : 'My Library'}
                </h1>
                <p data-testid="mylib-member-since" style={{ fontFamily: "'Space Mono', monospace", fontSize: 13, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--a1)', margin: '0 0 14px' }}>
                    {memberSince ? `Member since ${monthYear(memberSince)}` : 'My Library'}
                    {user?.membership_number ? ` · #${user.membership_number}` : ''}
                    {user?.membership_type === 'children' ? " · Children's membership" : ''}
                    {' · '}<Link to="/profile" data-testid="mylib-profile-link" style={{ color: 'var(--a3)', textDecoration: 'underline' }}>My profile →</Link>
                </p>

                {/* Quota tracker */}
                <div data-testid="mylib-books-out-count" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', margin: '0 0 28px' }}>
                    <span style={{ display: 'flex', gap: 5, alignItems: 'flex-end' }} aria-hidden="true">
                        {[0, 1, 2, 3, 4].map((i) => (
                            <span key={i} style={{ width: 13, height: 20 + (i % 2) * 3, border: '2px solid var(--line)', background: i < booksOut ? 'var(--a3)' : 'transparent', display: 'inline-block', boxSizing: 'border-box' }} />
                        ))}
                    </span>
                    <span style={{ fontFamily: "'Space Mono', monospace", fontWeight: 700, fontSize: 12, letterSpacing: 1.5, textTransform: 'uppercase' }}>{booksOut} of 5 books out</span>
                    {user?.child_membership_status === 'active' && (
                        <span style={{ fontFamily: "'Caveat', cursive", fontSize: 19, fontWeight: 600, color: 'var(--a5)', transform: 'rotate(-1.5deg)' }}>+ {user?.child_name || 'your child'}&apos;s books count separately</span>
                    )}
                </div>

                {/* Urgent actions banner */}
                {(() => {
                    const overdue = [...(myHolds.borrowed || []), ...(myHolds.delivered || [])].filter((h) => { const d = daysUntil(h.due_date); return d != null && d < 0; });
                    const fees = Number(user?.outstanding_fees || 0);
                    const subDays = user?.subscription_end_date ? daysUntil(user.subscription_end_date) : null;
                    const expSoon = user?.subscription_status === 'active' && subDays != null && subDays >= 0 && subDays <= 5;
                    if (!overdue.length && !(fees > 0) && !expSoon) return null;
                    const rowStyle = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap', padding: '13px 18px' };
                    const btnStyle = (a) => ({ cursor: 'pointer', border: '1px solid var(--line)', background: `var(${a})`, color: `var(--o${a.slice(-1)})`, fontFamily: "'Space Mono', monospace", fontWeight: 700, fontSize: 11, letterSpacing: 1.5, textTransform: 'uppercase', padding: '10px 18px' });
                    return (
                        <div data-testid="mylib-urgent-banner" style={{ border: '2px solid var(--a4)', background: 'var(--paper)', marginBottom: 28, boxShadow: '4px 5px 0 color-mix(in oklch, var(--a4) 30%, transparent)' }}>
                            {overdue.length > 0 && (
                                <div style={{ ...rowStyle, borderBottom: (fees > 0 || expSoon) ? '1px dashed var(--line)' : 'none' }} data-testid="banner-overdue">
                                    <span style={{ fontSize: 15 }}><b style={{ color: 'var(--a4)' }}>{overdue.length} book{overdue.length === 1 ? ' is' : 's are'} overdue</b> — return it soon or ask for more time.</span>
                                    <button onClick={() => { setOpenSection('shelves'); setExtendHold(overdue[0]); }} style={btnStyle('--a4')} data-testid="banner-extend-btn">Extend loan</button>
                                </div>
                            )}
                            {fees > 0 && (
                                <div style={{ ...rowStyle, borderBottom: expSoon ? '1px dashed var(--line)' : 'none' }} data-testid="banner-fees">
                                    <span style={{ fontSize: 15 }}><b style={{ color: 'var(--a4)' }}>₹{fees.toFixed(0)} in late fees</b> — clear them to keep borrowing.</span>
                                    <button onClick={() => setOpenSection('payments')} style={btnStyle('--a4')} data-testid="banner-payfees-btn">Pay now</button>
                                </div>
                            )}
                            {expSoon && (
                                <div style={rowStyle} data-testid="banner-renewal">
                                    <span style={{ fontSize: 15 }}><b style={{ color: 'var(--a2)' }}>Membership expires {subDays === 0 ? 'today' : `in ${subDays} day${subDays === 1 ? '' : 's'}`}</b> — renew to keep your books and queue spot.</span>
                                    <button onClick={() => setOpenSection('payments')} style={btnStyle('--a2')} data-testid="banner-renew-btn">Renew</button>
                                </div>
                            )}
                        </div>
                    );
                })()}

                {msg && <div style={{ padding: '10px 14px', border: '1px solid var(--a3)', background: 'color-mix(in oklch, var(--a3) 12%, transparent)', marginBottom: 20, fontSize: 14 }} data-testid="mylib-msg">{msg}</div>}

                {/* SHELVES DROPDOWN */}
                {(() => {
                    const activeCount = ['borrowed', 'delivered', 'reserved', 'pending', 'queued'].reduce((s, k) => s + (myHolds[k]?.length || 0), 0);
                    const totalEver = activeCount + (myHolds.returned?.length || 0);
                    const feesDue = Number(user?.outstanding_fees || 0);
                    return (
                        <>
                <Section title="My Shelves" badge={activeCount > 0 ? `${activeCount} active` : null} open={openSection === 'shelves'} onClick={() => setOpenSection(openSection === 'shelves' ? '' : 'shelves')} testid="section-shelves">
                    {totalEver === 0 && (
                        <div data-testid="shelves-empty-state" style={{ border: '2px solid var(--line)', borderRadius: '255px 25px 225px 25px / 25px 225px 25px 255px', padding: '38px 30px', textAlign: 'center', marginBottom: 40, background: 'var(--bg)' }}>
                            <p style={{ fontFamily: "'Caveat', cursive", fontSize: 28, fontWeight: 600, color: 'var(--a1)', margin: '0 0 8px', transform: 'rotate(-1deg)' }}>your shelves are waiting for their first story…</p>
                            <p style={{ fontSize: 15, opacity: 0.8, margin: '0 0 20px' }}>Browse the catalog, place a hold, and we&apos;ll have it ready for you at the library.</p>
                            <Link to="/catalog" className="hydlib-highlight" data-testid="shelves-empty-browse" style={{ display: 'inline-block', fontFamily: "'Space Mono', monospace", fontWeight: 700, fontSize: 12, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--ink)', padding: '10px 20px', background: `linear-gradient(104deg, transparent 1.5%, color-mix(in oklch, var(--a1) 40%, var(--paper)) 5%, color-mix(in oklch, var(--a1) 26%, var(--paper)) 52%, color-mix(in oklch, var(--a1) 45%, var(--paper)) 94%, transparent 98.5%)` }}>Browse the catalog →</Link>
                        </div>
                    )}
                    {(totalEver > 0 ? shelves : []).map((sh, i) => (
                        <div key={i} style={{ marginBottom: 44 }}>
                            <div style={{ display: 'flex', alignItems: 'baseline', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
                                <h2 style={{ fontFamily: "'Libre Caslon Display', serif", fontWeight: 400, fontSize: 26, margin: 0 }}>{sh.name}</h2>
                                {sh.badge || null}
                                <span style={{ fontFamily: "'Caveat', cursive", fontSize: 20, fontWeight: 600, color: sh.noteColor, transform: 'rotate(-2deg)' }}>{sh.note}</span>
                                <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: 1, opacity: 0.65, marginLeft: 'auto' }}>{sh.books.length} book{sh.books.length === 1 ? '' : 's'}</span>
                            </div>
                            {sh.books.length === 0 ? (
                                <p style={{ fontStyle: 'italic', opacity: 0.6, fontSize: 14, marginLeft: 4 }}>{sh.emptyText || 'Nothing here yet.'}</p>
                            ) : (
                                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 26, borderBottom: '6px solid color-mix(in oklch, var(--line) 70%, transparent)', overflowX: 'auto', padding: '4px 8px 0' }}>
                                    {sh.books.map((h) => {
                                        const action = sh.getAction?.(h);
                                        return (
                                            <div key={h.id} style={{ flexShrink: 0, width: 138, display: 'flex', flexDirection: 'column', alignItems: 'center' }} data-testid={`shelf-book-${h.id}`}>
                                                <Link to={h.book?.id ? `/book/${h.book.id}` : '#'} style={{ position: 'relative', width: 128, height: 186, border: '2px solid var(--line)', boxShadow: '5px 6px 0 color-mix(in oklch, var(--line) 28%, transparent)', boxSizing: 'border-box', background: 'var(--bg)', overflow: 'hidden' }}>
                                                    {h.book?.cover_image_url
                                                        ? <img src={h.book.cover_image_url} alt={h.book?.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                        : <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 10, textAlign: 'center', fontFamily: "'Libre Caslon Display', serif", fontSize: 14, color: 'var(--a1)' }}>{h.book?.title || 'Book'}</div>}
                                                </Link>
                                                <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', opacity: 0.85, marginTop: 10, textAlign: 'center', lineHeight: 1.3 }}>{(h.book?.title || '').slice(0, 40)}</div>
                                                {h.is_child_hold && (
                                                    <div data-testid={`child-hold-badge-${h.id}`} style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1, textTransform: 'uppercase', marginTop: 4, padding: '2px 6px', border: '1px dashed var(--a5)', color: 'var(--a5)' }}>For {h.child_name || 'child'}</div>
                                                )}
                                                <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, width: '100%' }}>
                                                    <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 11, textAlign: 'center', color: sh.getMetaColor(h) }}>{sh.getMeta(h)}</div>
                                                    {action && (
                                                        <button onClick={action.onClick} data-testid={`shelf-action-${h.id}`} className="hydlib-highlight" style={{ cursor: 'pointer', border: 'none', color: 'var(--ink)', fontFamily: "'Space Mono', monospace", fontWeight: 700, fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', padding: '5px 12px', background: `linear-gradient(104deg, transparent 1.5%, color-mix(in oklch, var(--a2) 58%, var(--paper)) 5%, color-mix(in oklch, var(--a2) 42%, var(--paper)) 52%, color-mix(in oklch, var(--a2) 64%, var(--paper)) 94%, transparent 98.5%)` }}>
                                                            {action.label}
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    ))}

                    {/* TBR + Librarian's picks */}
                    <div className="hydlib-cols" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 32, marginTop: 8 }}>
                        <div style={{ position: 'relative', border: '1px solid var(--line)', borderTop: '6px solid var(--a5)', background: 'var(--bg)', padding: '28px 26px' }}>
                            <h2 style={{ fontFamily: "'Libre Caslon Display', serif", fontWeight: 400, fontSize: 26, margin: '0 0 4px' }}>To Be Read</h2>
                            <p style={{ fontFamily: "'Caveat', cursive", fontSize: 20, fontWeight: 600, color: 'var(--a5)', margin: '0 0 18px', transform: 'rotate(-1.5deg)' }}>the pile that only grows</p>
                            {tbr.length === 0 ? (
                                <p style={{ fontStyle: 'italic', opacity: 0.6, fontSize: 14 }}>Empty. Tap ♡ on any book to add it.</p>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                    {tbr.map(t => {
                                        const bid = t.book_id ?? t.id;
                                        return (
                                            <div key={bid} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, borderBottom: '1px dashed var(--line)', paddingBottom: 8 }} data-testid={`tbr-${bid}`}>
                                                <Link to={`/book/${bid}`} style={{ fontSize: 15, flex: 1 }}>{t.title || 'Book'}</Link>
                                                <button onClick={() => removeTbr(bid)} style={{ cursor: 'pointer', border: 'none', background: 'none', color: 'var(--a4)', fontFamily: "'Space Mono', monospace", fontSize: 12 }}>✕</button>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                        <div style={{ position: 'relative', border: '1px solid var(--line)', borderTop: '6px solid var(--a1)', background: 'var(--bg)', padding: '28px 26px' }}>
                            <h2 style={{ fontFamily: "'Libre Caslon Display', serif", fontWeight: 400, fontSize: 26, margin: '0 0 4px' }}>From the librarian&apos;s desk</h2>
                            <p style={{ fontFamily: "'Caveat', cursive", fontSize: 20, fontWeight: 600, color: 'var(--a1)', margin: '0 0 18px', transform: 'rotate(-1.5deg)' }}>picked for you, {user?.name?.split(' ')[0] || 'reader'}</p>
                            {recs.length === 0 ? (
                                <p style={{ fontStyle: 'italic', opacity: 0.6, fontSize: 14 }}>Nothing here yet. Ask below and the librarian will pick a few for you.</p>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                                    {recs.map((r, i) => {
                                        const bookId = r.book_id || r.id;
                                        const title = r.book_title || r.title || 'A book';
                                        const authors = r.authors?.length ? ` — ${r.authors.join(', ')}` : '';
                                        return (
                                            <div key={r.id || i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, borderBottom: '1px dashed var(--line)', paddingBottom: 12 }} data-testid={`rec-${i}`}>
                                                <div>
                                                    {bookId ? <Link to={`/book/${bookId}`} style={{ fontFamily: "'Libre Caslon Display', serif", fontSize: 18, color: 'var(--ink)' }}>{title}</Link> : <div style={{ fontFamily: "'Libre Caslon Display', serif", fontSize: 18 }}>{title}</div>}
                                                    {authors && <div style={{ fontSize: 13, marginTop: 2, fontStyle: 'italic', opacity: 0.85 }}>{r.authors.join(', ')}</div>}
                                                    {r.note && <div style={{ fontSize: 14, marginTop: 4, fontStyle: 'italic', opacity: 0.85 }}>{r.note}</div>}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                            {/* Ask the librarian */}
                            <div style={{ marginTop: 18 }}>
                                {recPending ? (
                                    <p style={{ fontFamily: "'Space Mono', monospace", fontSize: 12, letterSpacing: 1, color: 'var(--a1)', margin: 0 }} data-testid="rec-request-pending">✉ Request sent — the librarian is on it.</p>
                                ) : askOpen ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                        <textarea
                                            value={recPrefs}
                                            onChange={(e) => setRecPrefs(e.target.value)}
                                            placeholder="Any mood, genre or authors you're after? (optional)"
                                            rows={2}
                                            style={{ width: '100%', boxSizing: 'border-box', border: '1px solid var(--line)', background: 'var(--paper)', padding: '10px 12px', fontSize: 14, color: 'var(--ink)', fontFamily: 'inherit', resize: 'vertical' }}
                                            data-testid="rec-prefs-input"
                                        />
                                        <div style={{ display: 'flex', gap: 10 }}>
                                            <button onClick={askLibrarian} disabled={askSubmitting} style={{ cursor: 'pointer', border: '1px solid var(--line)', background: 'var(--a1)', color: 'var(--o1)', fontFamily: "'Space Mono', monospace", fontWeight: 700, fontSize: 12, letterSpacing: 1.5, textTransform: 'uppercase', padding: '8px 16px', opacity: askSubmitting ? 0.6 : 1 }} data-testid="rec-request-submit">{askSubmitting ? 'Sending…' : 'Send request'}</button>
                                            <button onClick={() => setAskOpen(false)} style={{ cursor: 'pointer', border: '1px solid var(--line)', background: 'var(--paper)', color: 'var(--ink)', fontFamily: "'Space Mono', monospace", fontSize: 12, letterSpacing: 1.5, textTransform: 'uppercase', padding: '8px 16px' }}>Cancel</button>
                                        </div>
                                    </div>
                                ) : (
                                    <button onClick={() => setAskOpen(true)} className="hydlib-highlight" style={{ cursor: 'pointer', border: 'none', fontFamily: "'Space Mono', monospace", fontWeight: 700, fontSize: 12, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--ink)', padding: '9px 18px', background: `linear-gradient(104deg, transparent 1.5%, color-mix(in oklch, var(--a1) 40%, var(--paper)) 5%, color-mix(in oklch, var(--a1) 26%, var(--paper)) 52%, color-mix(in oklch, var(--a1) 45%, var(--paper)) 94%, transparent 98.5%)` }} data-testid="rec-ask-btn">{recs.length === 0 ? 'Ask the librarian →' : 'Ask for fresh picks →'}</button>
                                )}
                            </div>
                        </div>
                    </div>
                </Section>

                {/* MY EVENTS */}
                <Section title="My Events" badge={events.length > 0 ? `${events.length} upcoming` : null} open={openSection === 'events'} onClick={() => setOpenSection(openSection === 'events' ? '' : 'events')} testid="section-events">
                    {events.length === 0 ? (
                        <p style={{ fontStyle: 'italic', opacity: 0.7, fontSize: 15 }}>No upcoming events. <Link to="/events" style={{ color: 'var(--a3)' }}>Browse Sunday events →</Link></p>
                    ) : (
                        <div>
                            {events.map((ev, i) => {
                                const accents = ['--a1', '--a2', '--a3', '--a4', '--a5'];
                                const accent = accents[i % accents.length];
                                return (
                                    <div key={ev.id} data-testid={`myevent-${ev.id}`} style={{ display: 'inline-block', verticalAlign: 'top', background: `var(${accent})`, color: `var(--o${accent.slice(-1)})`, padding: '28px 26px 24px', width: 250, margin: '0 36px 36px 0', boxShadow: '3px 10px 16px color-mix(in oklch, var(--line) 40%, transparent)', transform: `rotate(${(i % 2 === 0 ? -1 : 1) * 1}deg)` }}>
                                        <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 12, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 10, opacity: 0.85 }}>{fmtDate(ev.event_datetime)}</div>
                                        <div style={{ fontFamily: "'Libre Caslon Display', serif", fontSize: 22, lineHeight: 1.25 }}>{ev.name}</div>
                                        {ev.description && <div style={{ fontSize: 13, marginTop: 10, opacity: 0.9, lineHeight: 1.55 }}>{ev.description.slice(0, 120)}</div>}
                                        <div style={{ fontFamily: "'Space Mono', monospace", fontWeight: 700, fontSize: 11, letterSpacing: 1, marginTop: 12 }}>You&apos;re in ✓</div>
                                        {ev.event_datetime && (
                                            <div style={{ marginTop: 8 }}>
                                                <a href={gcalUrl({ title: ev.name, start: ev.event_datetime, end: ev.event_end_datetime, description: ev.description, location: ev.location })} target="_blank" rel="noopener noreferrer" data-testid={`myevent-gcal-${ev.id}`} style={{ color: 'inherit', fontFamily: "'Space Mono', monospace", fontSize: 10.5, letterSpacing: 1, textTransform: 'uppercase', textDecoration: 'underline', opacity: 0.85 }}>+ Add to Google Calendar</a>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    <div style={{ marginTop: 16 }}>
                        <Link to="/events" className="hydlib-highlight" style={{ display: 'inline-block', fontFamily: "'Space Mono', monospace", fontWeight: 700, fontSize: 12, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--ink)', padding: '9px 18px', background: `linear-gradient(104deg, transparent 1.5%, color-mix(in oklch, var(--a1) 40%, var(--paper)) 5%, color-mix(in oklch, var(--a1) 26%, var(--paper)) 52%, color-mix(in oklch, var(--a1) 45%, var(--paper)) 94%, transparent 98.5%)` }}>Browse all events →</Link>
                    </div>
                </Section>

                {/* PAYMENTS */}
                <Section
                    title="Payments"
                    badge={feesDue > 0 ? `₹${feesDue.toFixed(0)} due` : (user?.subscription_status === 'active' ? 'active' : null)}
                    badgeColor={feesDue > 0 ? 'var(--a4)' : 'var(--a3)'}
                    open={openSection === 'payments'} onClick={() => setOpenSection(openSection === 'payments' ? '' : 'payments')} testid="section-payments"
                >
                    {/* Full payments panel — subscription, delivery balance, gifts, pause, add-ons */}
                    <div style={{ marginBottom: 32 }}>
                        <PaymentsPanel initialPlan={payPlan} initialChildName={searchParams.get('child_name') || ''} initialAdults={parseInt(searchParams.get('adults') || '0', 10)} initialChildren={parseInt(searchParams.get('children') || '0', 10)} />
                    </div>

                    {/* Payment history */}
                    <h3 style={{ fontFamily: "'Libre Caslon Display', serif", fontWeight: 400, fontSize: 22, margin: '0 0 16px' }}>Payment history</h3>
                    {payments.length === 0 ? (
                        <p style={{ fontStyle: 'italic', opacity: 0.7, fontSize: 15 }}>No payment history yet.</p>
                    ) : (
                        <div style={{ overflowX: 'auto', marginBottom: 20 }}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'auto auto auto 1fr', gap: '10px 24px', fontSize: 14, alignItems: 'center', minWidth: 560 }}>
                                {['Date', 'Amount', 'Status', 'Valid period'].map(h => (
                                    <div key={h} style={{ fontFamily: "'Space Mono', monospace", fontWeight: 700, fontSize: 11, letterSpacing: 1.5, textTransform: 'uppercase', opacity: 0.7, borderBottom: '1px solid var(--line)', paddingBottom: 6 }}>{h}</div>
                                ))}
                                {payments.map((p, i) => {
                                    const rupees = Math.round((Number(p.amount) || 0) / 100);
                                    const status = p.status || '';
                                    const statusColor = status === 'success' ? 'var(--a3)' : (status === 'pending' ? 'var(--a4)' : 'var(--ink)');
                                    const period = (p.subscription_start_date && p.subscription_end_date)
                                        ? `${fmtDate(p.subscription_start_date)} – ${fmtDate(p.subscription_end_date)}`
                                        : (p.subscription_end_date ? `Until ${fmtDate(p.subscription_end_date)}` : (p.type || p.payment_type || '—'));
                                    const cell = { borderBottom: '1px dashed var(--line)', paddingBottom: 8, fontFamily: "'Space Mono', monospace", fontSize: 13 };
                                    return (
                                        <div key={p.id || i} style={{ display: 'contents' }} data-testid={`payment-${i}`}>
                                            <div style={cell}>{fmtDate(p.created_at || p.paid_at)}</div>
                                            <div style={cell}>₹{rupees.toLocaleString('en-IN')}</div>
                                            <div style={cell}><span style={{ fontWeight: 700, fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', color: statusColor }}>{status}</span></div>
                                            <div style={cell}>{period}</div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </Section>
                        </>
                    );
                })()}
            </div>

            {/* REVIEW MODAL */}
            {extendHold && (
                <div style={{ position: 'fixed', inset: 0, background: 'color-mix(in oklch, var(--ink) 55%, transparent)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 20 }} onClick={() => setExtendHold(null)}>
                    <div style={{ background: 'var(--paper)', color: 'var(--ink)', border: '1px solid var(--line)', boxShadow: '8px 9px 0 color-mix(in oklch, var(--line) 35%, transparent)', padding: '28px 30px', maxWidth: 440, width: '100%' }} onClick={(e) => e.stopPropagation()} data-testid="extend-modal">
                        <h3 style={{ fontFamily: "'Libre Caslon Display', serif", fontWeight: 400, fontSize: 24, margin: '0 0 6px' }}>Extend this loan</h3>
                        <p style={{ fontSize: 14, opacity: 0.8, margin: '0 0 18px' }}>
                            <span style={{ fontStyle: 'italic' }}>{extendHold.book?.title || 'This book'}</span> — how many extra days would you like? Staff will confirm your request.
                        </p>
                        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                            {[7, 14, 21].map((d) => (
                                <button key={d} onClick={() => requestExtension(extendHold.id, d)} data-testid={`extend-days-${d}`} style={{ cursor: 'pointer', border: '1px solid var(--ink)', background: 'var(--bg)', color: 'var(--ink)', fontFamily: "'Space Mono', monospace", fontWeight: 700, fontSize: 12, letterSpacing: 1, padding: '10px 18px' }}>
                                    +{d} days
                                </button>
                            ))}
                            <button onClick={() => setExtendHold(null)} data-testid="extend-cancel" style={{ cursor: 'pointer', border: '1px solid var(--line)', background: 'transparent', color: 'var(--ink)', fontFamily: "'Space Mono', monospace", fontSize: 12, letterSpacing: 1, padding: '10px 14px' }}>
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {reviewHold && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 100, background: 'color-mix(in oklch, var(--ink) 45%, transparent)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={() => setReviewHold(null)} data-testid="review-modal">
                    <div style={{ position: 'relative', background: 'var(--paper)', border: '1px solid var(--line)', borderTop: '6px solid var(--a1)', padding: '32px 34px', width: 460, maxWidth: '100%', boxShadow: '8px 10px 0 color-mix(in oklch, var(--line) 35%, transparent)' }} onClick={(e) => e.stopPropagation()}>
                        <span style={{ position: 'absolute', top: -16, right: 18, fontFamily: "'Caveat', cursive", fontSize: 22, fontWeight: 600, color: 'var(--a1)', transform: 'rotate(-2deg)', background: 'var(--bg)', padding: '0 8px' }}>so, how was it?</span>
                        <h2 style={{ fontFamily: "'Libre Caslon Display', serif", fontWeight: 400, fontSize: 26, margin: '0 0 4px' }}>Leave a review</h2>
                        <p style={{ fontSize: 14, opacity: 0.8, margin: '0 0 18px', fontStyle: 'italic' }}>{reviewHold.book?.title}</p>
                        <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
                            {[1, 2, 3, 4, 5].map(n => (
                                <button key={n} onClick={() => setReviewRating(n)} data-testid={`review-star-${n}`} style={{ cursor: 'pointer', border: 'none', background: 'none', fontSize: 28, lineHeight: 1, color: n <= reviewRating ? 'var(--a5)' : 'color-mix(in oklch, var(--line) 60%, transparent)', padding: 0 }} aria-label={`${n} star${n === 1 ? '' : 's'}`}>★</button>
                            ))}
                        </div>
                        <textarea
                            value={reviewText}
                            onChange={(e) => setReviewText(e.target.value)}
                            placeholder="What did you think? (optional)"
                            rows={4}
                            style={{ width: '100%', boxSizing: 'border-box', border: '1px solid var(--line)', background: 'var(--bg)', padding: '11px 14px', fontSize: 15, color: 'var(--ink)', fontFamily: 'inherit', resize: 'vertical', marginBottom: 18 }}
                            data-testid="review-text"
                        />
                        <div style={{ display: 'flex', gap: 10 }}>
                            <button onClick={submitReview} disabled={reviewSubmitting} data-testid="review-submit" style={{ cursor: 'pointer', border: '1px solid var(--line)', background: 'var(--a1)', color: 'var(--o1)', fontFamily: "'Space Mono', monospace", fontWeight: 700, fontSize: 12, letterSpacing: 1.5, textTransform: 'uppercase', padding: '9px 18px', opacity: reviewSubmitting ? 0.6 : 1 }}>{reviewSubmitting ? 'Submitting…' : 'Submit review'}</button>
                            <button onClick={() => setReviewHold(null)} data-testid="review-cancel" style={{ cursor: 'pointer', border: '1px solid var(--line)', background: 'var(--paper)', color: 'var(--ink)', fontFamily: "'Space Mono', monospace", fontSize: 12, letterSpacing: 1.5, textTransform: 'uppercase', padding: '9px 18px' }}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}
        </PublicLayout>
    );
}

function Section({ title, badge, badgeColor, open, onClick, testid, children }) {
    return (
        <div style={{ marginBottom: 20 }} data-testid={testid}>
            <button
                onClick={onClick}
                style={{ cursor: 'pointer', width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, textAlign: 'left', border: '1px solid var(--line)', borderBottom: open ? 'none' : '1px solid var(--line)', background: 'var(--paper)', color: 'var(--ink)', padding: '20px 26px' }}
            >
                <span style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: "'Libre Caslon Display', serif", fontSize: 30 }}>{title}</span>
                    {badge && (
                        <span data-testid={`${testid}-badge`} style={{ fontFamily: "'Space Mono', monospace", fontWeight: 700, fontSize: 10.5, letterSpacing: 1.2, textTransform: 'uppercase', padding: '3px 10px', border: `1.5px solid ${badgeColor || 'var(--a1)'}`, color: badgeColor || 'var(--a1)', borderRadius: 999 }}>{badge}</span>
                    )}
                </span>
                <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 20, color: 'var(--a1)' }}>{open ? '−' : '+'}</span>
            </button>
            {open && (
                <div style={{ border: '1px solid var(--line)', borderTop: 'none', background: 'var(--paper)', padding: '36px 32px' }}>
                    {children}
                </div>
            )}
        </div>
    );
}
